import {
  type Address,
  type AxiosRequestConfig,
  type Feature,
  formatFeatureToAddress,
  publicHttpClient,
} from '@ahm/api-wrappers-core';
import { apiRoutes } from '../api-routes';
import { publicEnv } from '@/config/public-env.config.mjs';

// gw-maps gateway — gọi TRỰC TIẾP từ browser, không qua Next API route.
// Gateway public: `access-control-allow-origin: *` và allow-headers gồm
// `authorization,content-type` nên preflight pass. KHÔNG gửi `x-api-key`
// (không nằm trong allow-headers → preflight fail).
//
// Bearer không bị gắn vào request này: interceptor của cả web và webinapp đều
// lọc qua `shouldAttachBearer`, chỉ attach cho host của NEXT_PUBLIC_API_URI.
const MAPS_BASE_URL = publicEnv.NEXT_PUBLIC_EP_API_URI.replace(/\/+$/, '');

const AUTOCOMPLETE_URL = `${MAPS_BASE_URL}${apiRoutes.place.autocomplete}`;

/** Số ký tự tối thiểu trước khi bắn request — tránh spam gateway khi đang gõ. */
const MIN_QUERY_LENGTH = 2;

export interface AutocompleteData {
  features: Feature[];
  source_list?: string[];
  type?: string;
}

export type AutocompleteParams = {
  input: string;
  lat?: number;
  lng?: number;
  mode?: string;
};

/** Gateway có thể trả feature lồng mảng — flatten rồi map sang Address. */
const toAddresses = (features: Feature[]): Address[] => {
  const predictions: Address[] = [];

  for (const feature of features) {
    const items = Array.isArray(feature) ? (feature as Feature[]) : [feature];
    for (const item of items) {
      const address = formatFeatureToAddress(item);
      if (address) predictions.push(address);
    }
  }

  return predictions;
};

/**
 * Autocomplete địa chỉ qua gw-maps `dplaces/web/v4/autocomplete`.
 *
 * Lỗi mạng/HTTP được THROW (không swallow thành `[]`) để react-query retry và
 * caller phân biệt được "không có kết quả" vs "gọi lỗi" — swallow sẽ khiến lỗi
 * bị cache thành mảng rỗng suốt staleTime.
 */
export const getAutocompleteFn = async (
  requestParams: AutocompleteParams,
  config: AxiosRequestConfig = {}
): Promise<Address[]> => {
  const { input, lat, lng } = requestParams;

  const text = input?.trim() ?? '';
  if (text.length < MIN_QUERY_LENGTH) return [];

  const params: Record<string, string | number> = { text };
  if (lat && lng) {
    params['focus.point.lat'] = lat;
    params['focus.point.lng'] = lng;
  }

  const response = await publicHttpClient.get<AutocompleteData>(
    AUTOCOMPLETE_URL,
    { params, ...config }
  );

  return toAddresses(response.data?.features ?? []);
};
